import React from "react";
import { Button, GridItem, useDisclosure } from "@chakra-ui/react";
import { IoAdd } from "react-icons/io5";
import {
  AutoComplete,
  DatePicker,
  Form,
  Grid,
  Modal,
  NumberInput,
  Select,
  TextInput
} from "components";
import { DOSE_UNIT_OPTIONS, ROUTE_OPTIONS, useToast } from "duck";
import { MedicationCalculationTypeEnum } from "requests";
import { FormValues, getValidation } from "./duck";

interface CreateMedicationProps {
  calculationType: MedicationCalculationTypeEnum;
  defaultConcentrations?: Record<string, number>;
  successToast: string;
  onSubmit: (values: FormValues) => Promise<any>;
  isProtocol?: boolean;
}

const CreateMedication: React.FC<CreateMedicationProps> = ({
  calculationType,
  defaultConcentrations,
  successToast,
  onSubmit,
  isProtocol = false
}) => {
  const { onClose, onOpen, isOpen } = useDisclosure();

  const toast = useToast({
    status: "success",
    description: successToast
  });

  const isNonEmergencyMedication =
    calculationType === MedicationCalculationTypeEnum.Additional ||
    calculationType === MedicationCalculationTypeEnum.Induction ||
    calculationType === MedicationCalculationTypeEnum.PreAnesthetic;

  return (
    <>
      <Button onClick={onOpen} leftIcon={<IoAdd />}>
        Add
      </Button>

      <Modal
        isCentered={calculationType !== MedicationCalculationTypeEnum.Induction}
        title="Medication"
        onClose={onClose}
        isOpen={isOpen}
      >
        <Form<FormValues>
          {...getValidation(isNonEmergencyMedication)}
          onSubmit={values =>
            onSubmit(values).then(() => {
              onClose();
              toast();
            })
          }
        >
          {({ setValue }) => (
            <>
              <Grid py={6} templateColumns="repeat(2, 1fr)">
                <GridItem colSpan={2}>
                  <AutoComplete.Medications
                    creatable
                    name="name"
                    label="Name"
                    placeholder="Name"
                    optionValue="name"
                    onChange={(value, opt) => {
                      setValue("name", value as any, { shouldValidate: true });
                      if (defaultConcentrations) {
                        setValue(
                          "concentration",
                          opt?.name
                            ? defaultConcentrations[opt.name.split(" ")[0]]
                            : 0,
                          { shouldValidate: true }
                        );
                      }
                    }}
                  />
                </GridItem>
                <GridItem>
                  <NumberInput
                    name="dose"
                    label="Dose"
                    placeholder="Dose"
                    min={0}
                  />
                </GridItem>
                <GridItem mt={8}>
                  <Select
                    name="doseUnit"
                    label="Dose Unit"
                    labelHidden
                    options={DOSE_UNIT_OPTIONS}
                  />
                </GridItem>
                <GridItem colSpan={2}>
                  <NumberInput
                    name="concentration"
                    label="Concentration"
                    placeholder="Concentration"
                    min={0}
                  />
                </GridItem>
                <GridItem colSpan={2}>
                  <Select
                    label="Route"
                    name="route"
                    placeholder="Route"
                    options={ROUTE_OPTIONS}
                  />
                </GridItem>
                {!isProtocol && (
                  <GridItem colSpan={2}>
                    <DatePicker
                      label="Time Administered"
                      name="timeAdministered"
                      showTimeSelect
                      showTimeSelectOnly
                      placeholderText="Time Administered"
                      dateFormat="h:mm aaaa"
                    />
                  </GridItem>
                )}
                {calculationType ===
                  MedicationCalculationTypeEnum.Induction && (
                  <>
                    <GridItem colSpan={2}>
                      <NumberInput
                        name="volumeAdministered"
                        label="Volume administered"
                        placeholder="Volume administered"
                        min={0}
                      />
                    </GridItem>
                    <GridItem colSpan={2}>
                      <NumberInput
                        name="additionalVolumeDrawn"
                        label="Additional Volume Drawn"
                        placeholder="Additional Volume Drawn"
                        min={0}
                      />
                    </GridItem>
                    <GridItem colSpan={2}>
                      <NumberInput
                        name="additionalVolumeAdministered"
                        label="Additional Volume Administered"
                        placeholder="Additional Volume Administered"
                        min={0}
                      />
                    </GridItem>
                  </>
                )}
                {!isProtocol && (
                  <GridItem colSpan={2}>
                    <TextInput
                      label="Initials"
                      name="initials"
                      placeholder="Initials"
                    />
                  </GridItem>
                )}
              </Grid>
              <Form.Controls submitText="Add Medication">
                <Button variant="outline" colorScheme="gray" onClick={onClose}>
                  Cancel
                </Button>
              </Form.Controls>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default CreateMedication;
